import { EncryptionPolicy, KeyGenerationPolicy } from "../BayunCore";
import BayunError from "../constants/BayunError";
import SharedPreferences from "../constants/SharedPreferences";
import {
  getMemberLockBox,
  getCurrentTimeFromServer,
  getGroupPublicKey,
} from "../rest/BayunAPIManager";
import { getGroupKeyFromId } from "../rest/BayunGroupManager";
import { LockedObject } from "../thrift/LockedObject_types";
import { deserializeThrift } from "../thrift/ThriftFormatter";
import {
  getEncryptionModeFromString,
  getFromStorage,
  binaryToString,
  stringToBinary,
  getKeyGenerationPolicyFromString,
  getEncryptionPolicyFromString,
  checkForExpiry,
} from "../util/Utility";
import { decryptData, encryptData } from "./CryptManager";
import { updateAndSaveStatistics } from "./StatisticsManager";

export const lockText = async (
  sessionId,
  text,
  encryptionPolicy,
  keyGenerationPolicy,
  groupId,
  lockImage,
) => {
  if (!encryptionPolicy && encryptionPolicy != EncryptionPolicy.NONE) {
    encryptionPolicy = await getFromStorage(
      sessionId,
      SharedPreferences.DEFAULT_ENCRYPTION_POLICY,
    );

    if (encryptionPolicy == "Org") {
      encryptionPolicy = EncryptionPolicy.ORG;
    } else if (encryptionPolicy == "Member") {
      encryptionPolicy = EncryptionPolicy.MEMBER;
    } else if (encryptionPolicy == "Group") {
      encryptionPolicy = EncryptionPolicy.GROUP;
    } else if (encryptionPolicy == "GROUP_ASYMMETRIC") {
      encryptionPolicy = EncryptionPolicy.GROUP_ASYMMETRIC;
    }
  }

  if (!keyGenerationPolicy) {
    keyGenerationPolicy = await getFromStorage(
      sessionId,
      SharedPreferences.DEFAULT_KEY_GENERATION_POLICY,
    );

    if (keyGenerationPolicy == "Static") {
      keyGenerationPolicy = KeyGenerationPolicy.STATIC;
    } else if (keyGenerationPolicy == "Envelope") {
      keyGenerationPolicy = KeyGenerationPolicy.ENVELOPE;
    } else if (keyGenerationPolicy == "Chain") {
      keyGenerationPolicy = KeyGenerationPolicy.CHAIN;
    }
  }

  if (EncryptionPolicy.DEFAULT == encryptionPolicy) {
    encryptionPolicy = getEncryptionPolicyFromString(
      await getFromStorage(
        sessionId,
        SharedPreferences.DEFAULT_ENCRYPTION_POLICY,
      ),
    );
  }

  if (KeyGenerationPolicy.DEFAULT == keyGenerationPolicy) {
    // keyGenerationPolicy = await getFromLocalStorage(
    //   sessionId,
    //   SharedPreferences.DEFAULT_KEY_GENERATION_POLICY
    // );
    keyGenerationPolicy = getKeyGenerationPolicyFromString(
      await getFromStorage(
        sessionId,
        SharedPreferences.DEFAULT_KEY_GENERATION_POLICY,
      ),
    );
  }

  if (
    (encryptionPolicy === EncryptionPolicy.GROUP ||
      encryptionPolicy === EncryptionPolicy.GROUP_ASYMMETRIC) &&
    !groupId
  ) {
    throw BayunError.ERROR_GROUP_ID_CANNOT_BE_NULL;
  }

  switch (encryptionPolicy) {
    case EncryptionPolicy.NONE: {
      const orgKey = await getFromStorage(sessionId, SharedPreferences.ORG_KEY);
      if (!orgKey) {
        await getMemberLockBox(
          sessionId,
          await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
        );
      }
      break;
    }

    case EncryptionPolicy.ORG: {
      const orgKey = await getFromStorage(sessionId, SharedPreferences.ORG_KEY);
      if (!orgKey) {
        await getMemberLockBox(
          sessionId,
          await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
        );
      }
      break;
    }

    case EncryptionPolicy.MEMBER: {
      const memberKey = await getFromStorage(
        sessionId,
        SharedPreferences.MEMBER_KEY,
      );

      if (!memberKey) {
        await getMemberLockBox(
          sessionId,
          await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
        );
      }
      break;
    }

    case EncryptionPolicy.GROUP: {
      const groupKeys = await getGroupKeyFromId(sessionId, groupId);
      const groupEncryptionKey = groupKeys.key;

      if (!groupEncryptionKey) {
        await getMemberLockBox(
          sessionId,
          await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
        );
      }
      break;
    }

    case EncryptionPolicy.GROUP_ASYMMETRIC: {
      const PublicGroupKey = await getGroupPublicKey(sessionId, groupId);

      const groupKeys = await getGroupKeyFromId(sessionId, groupId);
      const groupEncryptionKey = groupKeys.key;

      if (!groupEncryptionKey) {
        await getMemberLockBox(
          sessionId,
          await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
        );
      }
      break;
    }
  }

  const result = await getLockedText(
    sessionId,
    text,
    encryptionPolicy,
    keyGenerationPolicy,
    groupId,
    lockImage,
  );

  await updateAndSaveStatistics(sessionId, false, false, true, false);
  return result;
};

export const unlockText = async (sessionId, lockedText, unlockImage) => {
  // Deserialize lockedText
  const lockedObject = deserializeThrift(lockedText, new LockedObject());

  if (lockedObject) {
    switch (lockedObject.encryptionPolicy) {
      case EncryptionPolicy.ORG: {
        const orgKey = await getFromStorage(
          sessionId,
          SharedPreferences.ORG_KEY,
        );

        if (!orgKey) {
          await getMemberLockBox(
            sessionId,
            await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
          );
        }
        break;
      }

      case EncryptionPolicy.MEMBER: {
        const memberKey = await getFromStorage(
          sessionId,
          SharedPreferences.MEMBER_KEY,
        );

        if (!memberKey) {
          await getMemberLockBox(
            sessionId,
            await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
          );
        }
        break;
      }

      case EncryptionPolicy.GROUP: {
        const groupKeys = await getGroupKeyFromId(
          sessionId,
          lockedObject.groupId,
        );
        const groupEncryptionKey = groupKeys.key;

        if (!groupEncryptionKey) {
          await getMemberLockBox(
            sessionId,
            await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
          );
        }
        break;
      }

      case EncryptionPolicy.GROUP_ASYMMETRIC: {
        const groupKeys = await getGroupKeyFromId(
          sessionId,
          lockedObject.groupId,
        );

        const groupEncryptionKey = groupKeys.key;

        if (!groupEncryptionKey) {
          await getMemberLockBox(
            sessionId,
            await getFromStorage(sessionId, SharedPreferences.ORG_MEMBER_ID),
          );
        }
        break;
      }
    }

    const result = await getUnlockedText(sessionId, lockedObject, unlockImage);

    await updateAndSaveStatistics(sessionId, false, false, false, false);
    return result;
  } else {
    console.log("Deserialized LockedObject was null.");
    throw BayunError.ERROR_SOMETHING_WENT_WRONG;
  }
};

async function getLockedText(
  sessionId,
  text,
  encryptionPolicy,
  keyGenerationPolicy,
  groupId,
  lockImage,
) {
  const encryptionMode = await getFromStorage(
    sessionId,
    SharedPreferences.DEFAULT_ENCRYPTION_MODE,
  );
  const publicKey = await getFromStorage(
    sessionId,
    SharedPreferences.MEMBER_PUBLIC_KEY,
  );

  return await encryptData(
    sessionId,
    text,
    encryptionPolicy,
    keyGenerationPolicy,
    getEncryptionModeFromString(encryptionMode),
    groupId,
    lockImage,
    publicKey,
  );
}

async function getUnlockedText(sessionId, lockedObject, unlockImage) {
  return await decryptData(sessionId, lockedObject, unlockImage);
}

export const lockData = async (
  sessionId,
  text,
  encryptionPolicy,
  keyGenerationPolicy,
  groupId,
) => {
  var binString = binaryToString(text);

  var result = await lockText(
    sessionId,
    binString,
    encryptionPolicy,
    keyGenerationPolicy,
    groupId,
  );

  var stringBin = stringToBinary(result);

  return stringBin;
};

export const unlockData = async (sessionId, lockedData) => {
  var binString = binaryToString(lockedData);
  var result = await unlockText(sessionId, binString);
  var stringBin = stringToBinary(result);
  return stringBin;
};
